import api from "@/lib/api";
import { saveNavState, snapshotNavState } from "@/lib/navState";

// Item 13 — Drillthrough framework (client side). One config shape, context building,
// field mappings, URL tokens, RLS/CLS-safe navigation via the server resolvers,
// modal / same-tab / new-tab modes, filter preserve-ignore-override, conditional availability,
// dynamic titles and A → B → C chaining with a state-preserving Back.

export const OPEN_MODES = ["same_tab", "new_tab", "modal"];
export const FILTER_MODES = ["preserve", "ignore", "override"];

export function normalizeDrillthrough(dt) {
  const d = dt || {};
  let targets = Array.isArray(d.targets) ? d.targets : [];
  if (!targets.length && d.dashboard_id) {
    targets = [{ id: "t1", mode: "dashboard", dashboard_id: d.dashboard_id, dashboard_name: d.dashboard_name, label: d.dashboard_name || "", open: d.open || "same_tab" }];
  }
  return {
    enabled: !!d.enabled,
    trigger: d.trigger || "right_click",
    targets: targets.filter(Boolean).map((t, i) => {
      const carry = { clicked: true, widget_filters: true, global_filters: true, ...(t.carry || {}) };
      // legacy configs only had carry.global_filters — derive the explicit filter mode from it
      const filter_mode = FILTER_MODES.includes(t.filter_mode) ? t.filter_mode : (carry.global_filters === false ? "ignore" : "preserve");
      return {
        id: t.id || `t${i + 1}`,
        mode: t.mode === "url" ? "url" : "dashboard",
        label: t.label || "",
        dashboard_id: t.dashboard_id || "",
        dashboard_name: t.dashboard_name || "",
        url: t.url || "",
        open: OPEN_MODES.includes(t.open) ? t.open : "same_tab",
        title: t.title || "",                                   // dynamic page title template
        filter_mode,
        require_fields: (t.require_fields || []).filter(Boolean), // conditional availability
        carry: { ...carry, global_filters: filter_mode !== "ignore" },
        mappings: (t.mappings || []).filter((m) => m && m.source && m.target),
      };
    }),
  };
}

export const targetLabel = (t) => t.label || (t.mode === "url" ? (t.url || "External link") : (t.dashboard_name || "Dashboard"));

// Conditional availability: a target is only offered when every required field carries a value.
export function targetAvailability(t, filters = []) {
  const need = (t.require_fields || []).filter(Boolean);
  if (!need.length) return { ok: true };
  const have = new Set((filters || []).filter((f) => f && f.value != null && f.value !== "" && !(Array.isArray(f.value) && !f.value.length)).map((f) => f.field));
  const missing = need.filter((f) => !have.has(f));
  return missing.length ? { ok: false, reason: `needs ${missing.join(", ")}` } : { ok: true };
}

export function encodeDrill(payload) {
  return encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(payload)))));
}
export function decodeDrill(param) {
  try {
    if (!param) return null;
    const d = JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(param)))));
    return d && Array.isArray(d.f) && d.f.length ? d : null;
  } catch { return null; }
}

const usable = (f) => f && f.field && f.value != null && f.value !== "" && !(Array.isArray(f.value) && !f.value.length);
const plain = ({ dataset_id: _d, __src, __scope, __dt, key, ...r }) => r;

// Filters carried from a clicked data point: every dimension the point identifies
// (x-axis / category, series or legend split, extra dims for tables & pivots) + optional widget / global filters.
export function buildDrillContext({ params, dims = [], widgetFilters = [], globalFilters = [], carry = {}, row = null }) {
  const clicked = [];
  const dimOk = (d) => d && d.field && (!d.date_part || d.date_part === "none");
  if (carry.clicked !== false) {
    if (row && typeof row === "object") {
      dims.forEach((d) => { if (dimOk(d) && row[d.field] != null && row[d.field] !== "") clicked.push({ field: d.field, op: "eq", value: row[d.field] }); });
    } else if (params) {
      const [d0, d1] = dims;
      const v0 = params.name ?? (Array.isArray(params.value) ? params.value[0] : undefined);
      if (dimOk(d0) && v0 != null && v0 !== "") clicked.push({ field: d0.field, op: "eq", value: v0 });
      if (dimOk(d1) && params.seriesName && params.seriesName !== params.name && dims.length > 1) clicked.push({ field: d1.field, op: "eq", value: params.seriesName });
    }
  }
  const wf = carry.widget_filters === false ? [] : (widgetFilters || []).filter(usable).map(plain);
  const gf = carry.global_filters === false ? [] : (globalFilters || []).filter(usable).map(plain);
  // clicked values win over broader filters on the same field
  const seen = new Set(clicked.map((f) => f.field));
  return [...clicked, ...[...wf, ...gf].filter((f) => !seen.has(f.field) && (seen.add(f.field), true))];
}

export function applyMappings(filters, mappings = []) {
  const ren = Object.fromEntries((mappings || []).filter((m) => m.source && m.target).map((m) => [m.source, m.target]));
  return (filters || []).map((f) => ({ ...f, field: ren[f.field] || f.field }));
}

function tokenValues(filters) {
  const byField = {};
  (filters || []).forEach((f) => { if (byField[f.field] === undefined) byField[f.field] = Array.isArray(f.value) ? f.value.join(", ") : f.value; });
  return byField;
}

export function fillUrlTokens(url, filters) {
  const byField = tokenValues(filters);
  return String(url || "").replace(/\{([A-Za-z0-9_ .:-]+)\}/g, (m, k) => (byField[k] !== undefined ? encodeURIComponent(String(byField[k])) : ""));
}

// Dynamic page title: "Sales — {region} {year}" resolved against the carried context.
export function fillTitle(tpl, filters) {
  if (!tpl) return "";
  const byField = tokenValues(filters);
  return String(tpl).replace(/\{([A-Za-z0-9_ .:-]+)\}/g, (m, k) => (byField[k] !== undefined ? String(byField[k]) : "")).replace(/\s{2,}/g, " ").trim();
}

function go(url, open) {
  if (open === "new_tab") window.open(url, "_blank", "noopener");
  else window.location.assign(url);
}

function buildPayload({ mapped, from, sourceId, target, trail, backPath }) {
  const bk = saveNavState({ ...(snapshotNavState() || {}), path: backPath, title: from });
  const step = { title: from || "Dashboard", path: backPath, bk, src: sourceId || "" };
  return {
    f: mapped,
    from,
    src: sourceId || "",
    t: target.id,
    mode: target.filter_mode || "preserve",
    ttl: fillTitle(target.title, mapped),
    bk,
    trail: [...(trail || []), step].slice(-6),
  };
}

// Resolve + navigate. Returns {ok, error, stripped, modal}. Anonymous viewers go through the public
// resolver (target must be public + published); signed-in users get an access check and CLS-hidden
// fields removed. `open: "modal"` returns the resolved url for an in-page dialog instead of navigating.
export async function navigateDrill(target, filters, { from = "", sourceId = "", publicToken = "", trail = null, backPath = "" } = {}) {
  const mapped = applyMappings(filters, target.mappings);
  const avail = targetAvailability(target, mapped);
  if (!avail.ok) return { ok: false, error: `This drillthrough ${avail.reason}` };
  const here = backPath || (typeof window !== "undefined" ? window.location.pathname + window.location.search.replace(/([?&])dt=[^&]*/g, "").replace(/^&/, "?") : "");
  if (target.mode === "url") {
    const url = fillUrlTokens(target.url, mapped);
    if (!/^https?:\/\//i.test(url)) return { ok: false, error: "Target URL must start with http(s)://" };
    if (target.open === "modal") return { ok: true, modal: { url, title: fillTitle(target.title, mapped) || targetLabel(target), external: true } };
    go(url, target.open);
    return { ok: true };
  }
  if (!target.dashboard_id) return { ok: false, error: "No target dashboard selected" };
  try {
    let path = "";
    let out = mapped;
    let stripped = [];
    if (publicToken) {
      const { data } = await api.get(`/public/dashboard/${publicToken}/drillthrough/${target.dashboard_id}`);
      path = data.path;
    } else {
      const { data } = await api.post("/drillthrough/resolve", { target_id: target.dashboard_id, filters: mapped, source_dashboard_id: sourceId || null });
      path = data.path;
      out = data.filters;
      stripped = data.stripped || [];
    }
    const payload = { ...buildPayload({ mapped: out, from, sourceId, target, trail, backPath: here }), stripped };
    const q = `dt=${encodeDrill(payload)}`;
    if (target.open === "modal") {
      return { ok: true, stripped, modal: { url: `${path}?${q}&dtmodal=1`, title: payload.ttl || target.dashboard_name || targetLabel(target), path, q } };
    }
    go(`${path}?${q}`, target.open);
    return { ok: true, stripped };
  } catch (e) {
    const det = e?.response?.data?.detail;
    return { ok: false, error: typeof det === "string" ? det : "Could not open the drillthrough target" };
  }
}
